/** biome-ignore-all lint/correctness/useExhaustiveDependencies: <this rule is error-prone!> */
import type { SxProps, Theme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import {
  type FormHTMLAttributes,
  type PropsWithChildren,
  type SubmitEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  type FieldError,
  type FieldValues,
  FormProvider,
  type Path,
  type RegisterOptions,
  type UseFormReturn,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { scrollToFirstErrorInForm } from '../utils/scrollToFirstErrorInForm';
import { FormSubmitProvider, Subject, useFormSubmitContext } from './context/FormSubmitContext';
import { ResourceInfoProvider } from './context/ResourceInfoContext';
import { useRHFNestedFormContext } from './context/RHFNestedFormContext';

const StyledForm = styled('form')`
  height: 100%;
`;

type FormProps<T extends FieldValues = Record<string, string>> = FormHTMLAttributes<HTMLFormElement> & {
  methods: UseFormReturn<T>;
  sx?: SxProps<Theme>;
  name?: string;
};

export const Form = <T extends FieldValues = Record<string, string>>(props: PropsWithChildren<FormProps<T>>) => {
  const outerFormMethods = useFormContext<T>();

  if (!outerFormMethods) {
    return <OuterForm<T> {...props} />;
  }
  return <InnerForm<T> {...props} />;
};

const OuterForm = <T extends FieldValues = Record<string, string>>({
  children,
  methods,
  name,
  ...props
}: PropsWithChildren<FormProps<T>>) => {
  const {
    formConfig: { noWrapper },
  } = useRHFNestedFormContext();

  const [submitClickCount, setSubmitClickCount] = useState(0);
  const selfSymbol = useRef<symbol>(Symbol('formTriggerSymbol'));
  const triggerMapRef = useRef<Map<symbol, UseFormReturn<T>['trigger']>>(new Map());
  const isValidating$Ref = useRef(new Subject<boolean>());

  useEffect(() => {
    triggerMapRef.current.set(selfSymbol.current, methods.trigger);
    return () => {
      triggerMapRef.current.delete(selfSymbol.current);
    };
  }, []);

  const validateAll: () => Promise<boolean> = useCallback(async () => {
    isValidating$Ref.current.next(true);
    methods.clearErrors();

    const result = await Promise.all(
      triggerMapRef.current
        .values()
        .toArray()
        .map(triggerFn => triggerFn())
    );
    isValidating$Ref.current.next(false);
    return result.every(maybeBoolean => !!maybeBoolean);
  }, []);

  const onSubmit = useMemo(() => {
    if (!props.onSubmit) return;
    return async (event: SubmitEvent<HTMLFormElement>) => {
      setSubmitClickCount(prev => prev + 1);

      event.preventDefault();
      event.stopPropagation();

      const formElement = event.currentTarget;

      await validateAll();

      /** Time, after validation, required to write errors up the root */
      setTimeout(() => {
        const {
          formState: { errors },
        } = methods;

        if (!isEmpty(errors)) {
          scrollToFirstErrorInForm(errors, {
            root: formElement,
          });

          return;
        }

        return props.onSubmit?.(event);
      }, 100);
    };
  }, [methods, props.onSubmit]);

  if (!methods) {
    return;
  }

  if (noWrapper || !onSubmit) {
    return <FormProvider {...methods}>{children}</FormProvider>;
  }

  return (
    <ResourceInfoProvider name={name || null}>
      <FormSubmitProvider
        submitClickCount={submitClickCount}
        submitClicked={submitClickCount > 0}
        triggerMap={triggerMapRef.current}
        isValidating$={isValidating$Ref.current}
      >
        <FormProvider {...methods}>
          <StyledForm {...props} onSubmit={onSubmit}>
            {children}
          </StyledForm>
        </FormProvider>
      </FormSubmitProvider>
    </ResourceInfoProvider>
  );
};

const InnerForm = <T extends FieldValues = Record<string, string>>({
  children,
  methods: innerFormMethods,
  onSubmit,
  ...props
}: PropsWithChildren<FormProps<T>>) => {
  const selfSymbol = useRef<symbol>(Symbol('formTriggerSymbol'));
  const { triggerMap, isValidating$ } = useFormSubmitContext();

  useEffect(() => {
    triggerMap?.set(selfSymbol.current, innerFormMethods.trigger);
    return () => {
      triggerMap?.delete(selfSymbol.current);
    };
  }, [innerFormMethods.trigger]);

  const {
    namespace,
    formConfig: { noWrapper },
  } = useRHFNestedFormContext();

  useEffect(() => {
    const upstreamErrors = (isValidating: boolean) => {
      if (isValidating) {
        return;
      }

      registeredFields.current.forEach(path => {
        const { error } = innerFormMethods.getFieldState(path);
        if (error) {
          outerFormMethods.setError(`${namespace}.${path}`, { message: (error.message as string) || 'invalid value' });
        } else {
          outerFormMethods.clearErrors(`${namespace}.${path}`);
        }
      });
    };

    return isValidating$?.subscribe(upstreamErrors);
  }, []);

  /** Outer Form */
  const outerFormMethods = useFormContext();
  const observableOuterFormData = useWatch({ control: outerFormMethods?.control });

  /** Inner Form */
  const innerFormData = useWatch<T>({ control: innerFormMethods.control });
  const registeredFields = useRef<Set<Path<T>>>(new Set());

  const enableOuterToInnerSync = useRef(false);

  useEffect(() => {
    if (enableOuterToInnerSync.current) {
      return;
    }

    if (
      innerFormData &&
      namespace &&
      observableOuterFormData[namespace] &&
      isEqual(innerFormData, observableOuterFormData[namespace])
    ) {
      enableOuterToInnerSync.current = true;
    }
  }, [innerFormData, observableOuterFormData, namespace]);

  const formStateProxy = useRef<any>(null);
  const [formMethodsProxy, setFormMethodsProxy] = useState<UseFormReturn<T>>();

  const formControlProxyFactory = useRef(() => {
    let registerFn;
    const registerProxy = (name: Path<T>, options?: RegisterOptions) => {
      registeredFields.current.add(name);
      return registerFn(name, options);
    };

    let unregisterFn;
    const unregisterProxy = (name: Path<T>, options?: RegisterOptions) => {
      registeredFields.current.delete(name);
      return unregisterFn(name, options);
    };

    return {
      get(obj, prop) {
        if (prop === 'register') {
          registerFn = obj[prop];
          return registerProxy;
        }

        if (prop === 'unregister') {
          unregisterFn = obj[prop];
          return unregisterProxy;
        }
        return obj[prop];
      },
    };
  });

  const formMethodsProxyFactory = useRef(() => {
    let setErrorFn;
    const setErrorProxy = (name, error: FieldError, options) => {
      outerFormMethods.setError(`${namespace}.${name}`, error);
      return setErrorFn?.(name, error, options);
    };

    let clearErrorsFn;
    const clearErrorsProxy = (name?: string | string[]) => {
      outerFormMethods.clearErrors(
        name && (typeof name === 'string' ? `${namespace}.${name}` : name.map(s => `${namespace}.${s}`))
      );
      return clearErrorsFn?.(name);
    };

    let setValueFn;
    const setValueProxy = (name, value, options) => {
      if (!isEqual(outerFormMethods.getValues(`${namespace}.${name}`), value)) {
        outerFormMethods.setValue(`${namespace}.${name}`, cloneDeep(value), options);
      }
      return setValueFn?.(name, value, options);
    };

    let controlProxy;

    let registerFn;
    const registerProxy = (name: Path<T>, options?: RegisterOptions) => {
      registeredFields.current.add(name);
      return registerFn(name, options);
    };

    let unregisterFn;
    const unregisterProxy = (name: Path<T>, options?: RegisterOptions) => {
      registeredFields.current.delete(name);
      return unregisterFn(name, options);
    };

    return {
      get(obj, prop) {
        if (prop === 'setError') {
          setErrorFn = obj[prop];
          return setErrorProxy;
        }

        if (prop === 'clearErrors') {
          clearErrorsFn = obj[prop];
          return clearErrorsProxy;
        }

        if (prop === 'setValue') {
          setValueFn = obj[prop];
          return setValueProxy;
        }
        if (prop === 'register') {
          registerFn = obj[prop];
          return registerProxy;
        }

        if (prop === 'unregister') {
          unregisterFn = obj[prop];
          return unregisterProxy;
        }

        if (prop === 'control') {
          if (!controlProxy) {
            controlProxy = new Proxy(obj[prop], formControlProxyFactory.current());
          }
          return controlProxy;
        }

        return obj[prop];
      },
    };
  });

  useEffect(() => {
    formStateProxy.current = null;
  }, [innerFormMethods.formState]);

  useEffect(() => {
    setFormMethodsProxy(new Proxy(innerFormMethods, formMethodsProxyFactory.current()));
  }, [innerFormMethods]);

  useEffect(() => {
    return () => {
      registeredFields.current.forEach(path => {
        outerFormMethods.clearErrors(`${namespace}.${path}`);
      });
    };
  }, []);

  const errorsJsonRef = useRef<string>('');
  useEffect(() => {
    const errorsJson = JSON.stringify(innerFormMethods.formState.errors);
    if (errorsJson === errorsJsonRef.current) {
      return;
    }
    errorsJsonRef.current = errorsJson;

    registeredFields.current.forEach(path => {
      const { error } = innerFormMethods.getFieldState(path);
      if (error) {
        outerFormMethods.setError(`${namespace}.${path}`, { message: (error.message as string) || 'invalid value' });
      } else {
        outerFormMethods.clearErrors(`${namespace}.${path}`);
      }
    });
  }, [innerFormMethods.formState]);

  useEffect(() => {
    requestAnimationFrame(() => {
      registeredFields.current.forEach(path => {
        const innerValue = innerFormMethods.getValues(path as Path<T>);
        const outerValue = outerFormMethods.getValues(`${namespace}.${path}`);

        if (!isEqual(innerValue, outerValue)) {
          const { invalid, isDirty, isTouched, error } = innerFormMethods.getFieldState(path as Path<T>);
          if (invalid || error) {
            outerFormMethods.setError(`${namespace}.${path}`, error || { message: 'invalid value' });
          } else {
            outerFormMethods.clearErrors(`${namespace}.${path}`);
          }

          outerFormMethods.setValue(`${namespace}.${path}`, innerValue, {
            shouldDirty: isDirty,
            shouldTouch: isTouched,
            shouldValidate: false,
          });
        }
      });
    });
  }, [innerFormData]);

  useEffect(() => {
    /** OuterForm data for given namespace should match InnerForm
     * data before writing from parent-to-children is available. */
    if (!enableOuterToInnerSync.current) {
      return;
    }
    const pathsToValidate: Path<T>[] = [];

    registeredFields.current.forEach(path => {
      const innerValue = innerFormMethods.getValues(path as Path<T>);
      const outerValue = outerFormMethods.getValues(`${namespace}.${path}`);

      if (!isEqual(innerValue, outerValue)) {
        pathsToValidate.push(path as Path<T>);
        innerFormMethods.setValue(path as Path<T>, outerValue, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
      }
    });

    requestAnimationFrame(() => {
      pathsToValidate.forEach(path => {
        const { invalid, error } = innerFormMethods.getFieldState(path as Path<T>);
        if (invalid || error) {
          outerFormMethods.setError(`${namespace}.${path}`, error || { message: 'invalid value' });
        }
      });
    });
  }, [observableOuterFormData]);

  if (!formMethodsProxy) {
    return;
  }

  if (noWrapper || !onSubmit) {
    return <FormProvider {...formMethodsProxy}>{children}</FormProvider>;
  }

  return (
    <FormProvider {...formMethodsProxy}>
      <StyledForm {...props} onSubmit={onSubmit}>
        {children}
      </StyledForm>
    </FormProvider>
  );
};
